import React from "react";
import { useQuery } from "@tanstack/react-query";
import { History } from "lucide-react";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";

// Reusable per-record Change History (financial/commercial field audit).
// entityType: "Asset" | "Lead" | "Deal" | "TaxonomyValue"
export default function ChangeHistory({ entityType, entityId }) {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["audit", entityType, entityId],
    queryFn: async () => (await api.get(`/audit-log/${entityType}/${entityId}`)).data,
    enabled: !!entityId,
  });

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="change-history">
      <div className="flex items-center gap-2">
        <History className="h-4 w-4 text-slate-500" />
        <div className="label-eyebrow">Change History</div>
      </div>
      <p className="text-xs text-slate-500 mt-1">Tracked changes to financial &amp; commercial fields.</p>
      {isLoading ? (
        <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-500 py-6 text-center" data-testid="change-history-empty">
          No tracked changes yet.
        </div>
      ) : (
        <div className="mt-3 divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.id} className="py-2.5 flex items-start gap-3 text-sm" data-testid={`audit-row-${r.id}`}>
              <div className="font-mono-num text-[11px] text-slate-500 w-32 shrink-0 pt-0.5">{formatDateTime(r.changed_at)}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900">{r.field_label}</div>
                <div className="text-slate-600">
                  <span className="text-slate-500">{r.old_value ?? "—"}</span>
                  <span className="mx-1.5 text-slate-400">→</span>
                  <span className="font-semibold text-slate-900">{r.new_value ?? "—"}</span>
                </div>
              </div>
              <div className="text-xs text-slate-500 shrink-0">by {r.changed_by_name || "system"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
